import { useCallback, useEffect, useRef, useState } from "react";
import {
  PROMPT_EXAMPLE_IMAGE_URLS,
} from "../data/promptExamplePhotos";

const ADVANCE_MS = 5000;
const EXIT_MS = 420;
const STACK_DEPTH = 3;

export type PromptImageJump = { id: number; index: number };

type PromptImageStackProps = {
  urls?: readonly string[];
  jump?: PromptImageJump | null;
  onJumpHandled?: () => void;
  /** Fired when the front card index changes (auto-advance, jump, or initial). */
  onFrontIndexChange?: (index: number) => void;
};

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function PromptImageStack({
  urls: urlsProp,
  jump = null,
  onJumpHandled,
  onFrontIndexChange,
}: PromptImageStackProps) {
  const urls = urlsProp ?? PROMPT_EXAMPLE_IMAGE_URLS;
  const [frontIndex, setFrontIndex] = useState(0);
  const [exiting, setExiting] = useState(false);
  const exitingRef = useRef(false);
  const onJumpHandledRef = useRef(onJumpHandled);
  onJumpHandledRef.current = onJumpHandled;
  const onFrontIndexChangeRef = useRef(onFrontIndexChange);
  onFrontIndexChangeRef.current = onFrontIndexChange;

  useEffect(() => {
    exitingRef.current = exiting;
  }, [exiting]);

  useEffect(() => {
    onFrontIndexChangeRef.current?.(frontIndex);
  }, [frontIndex]);

  useEffect(() => {
    if (!jump) return;
    exitingRef.current = false;
    setExiting(false);
    setFrontIndex(jump.index % urls.length);
    onJumpHandledRef.current?.();
  }, [jump, urls.length]);

  const advance = useCallback(() => {
    if (urls.length <= 1) return;
    if (exitingRef.current) return;
    if (prefersReducedMotion()) {
      setFrontIndex((i) => (i + 1) % urls.length);
      return;
    }
    setExiting(true);
  }, [urls.length]);

  useEffect(() => {
    if (urls.length <= 1) return;
    const id = window.setInterval(advance, ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [urls.length, advance]);

  useEffect(() => {
    if (!exiting) return;
    const id = window.setTimeout(() => {
      setExiting(false);
      setFrontIndex((i) => (i + 1) % urls.length);
    }, EXIT_MS);
    return () => window.clearTimeout(id);
  }, [exiting]);

  const visible = Math.min(STACK_DEPTH, urls.length);
  const cards = Array.from({ length: visible }, (_, depth) => {
    const urlIndex = (frontIndex + depth) % urls.length;
    return { depth, url: urls[urlIndex] };
  });

  return (
    <div
      className="relative mx-auto w-[min(100%,16rem)] max-w-[calc(100vw-2rem)] select-none sm:w-[15.5rem] sm:max-w-none"
      role="region"
      aria-roledescription="carousel"
      aria-label="Sample in-game photos"
    >
      <div
        className="relative aspect-[3/4] w-full"
        aria-live="polite"
        aria-atomic="true"
      >
        {cards.map(({ depth, url }) => {
          const isFront = depth === 0;
          const y = depth * 6;
          const scale = 1 - depth * 0.032;
          const rotateDeg = depth === 0 ? 0 : depth % 2 === 1 ? 2.5 : -2.5;
          const z = 40 - depth;

          const baseTransform = `translateY(${y}px) scale(${scale}) rotate(${rotateDeg}deg)`;
          const transform =
            isFront && exiting
              ? "translateX(-108%) translateY(4px) scale(0.98) rotate(-6deg)"
              : baseTransform;

          return (
            <div
              key={depth}
              className={[
                "absolute inset-0 overflow-hidden rounded-xl bg-[#141820] ring-1 ring-white/[0.08]",
                isFront
                  ? "shadow-[0_16px_40px_rgba(0,0,0,0.35)]"
                  : "shadow-[0_8px_24px_rgba(0,0,0,0.22)]",
                "transition-[transform,opacity] duration-[420ms] ease-out motion-reduce:transition-none",
                isFront && exiting ? "opacity-0" : "opacity-100",
              ].join(" ")}
              style={{
                zIndex: z,
                transform,
                transformOrigin: "center bottom",
              }}
            >
              <img
                src={url}
                alt=""
                width={600}
                height={800}
                className="h-full w-full object-cover"
                loading={depth === 0 ? "eager" : "lazy"}
                decoding="async"
                draggable={false}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
